import { ComponentType } from 'react';
import { MdOutlineDashboard, MdOutlineSettings } from 'react-icons/md';
import {
  PiBagSimple,
  PiBriefcase,
  PiBuildingsLight,
  PiCreditCard,
  PiHandshake,
  PiReceipt,
  PiShieldCheck,
  PiUserPlusLight,
  PiUsersFourLight,
} from 'react-icons/pi';
import Contacts from '@/contacts/Contacts';
import type { NavCounterKey } from '@/models/nav-counters';
import {
  CLIENT_PORTFOLIO_VIEW,
  INVOICING_COMPANIES_ALL,
  OFFER_LIST_PERMISSIONS,
  PAYERS_ALL,
  PERMISSIONS,
  Permission,
  PROPOSALS_READ_SHOP,
  REFERRING_AGENTS_ALL,
  REFERRING_AGENTS_READ,
} from '@/models/permissions';
import AccountDetailPage from '@/pages/accounts/AccountDetailPage';
import AccountsListPage from '@/pages/accounts/AccountsListPage';
import Roles from '@/pages/admin/Roles';
import Users from '@/pages/admin/Users';
import Companies from '@/pages/Companies';
import ClientCompaniesPage from '@/pages/client-companies/ClientCompaniesPage';
import ClientCompanyPage from '@/pages/client-companies/ClientCompanyPage';
import ClientContactPortfolioPage from '@/pages/client-contacts/ClientContactPortfolioPage';
import CommercialProposalsPage from '@/pages/commercial-proposals/CommercialProposalsPage';
import ContactDetailPage from '@/pages/contacts/ContactDetailPage';
import Dashboard from '@/pages/dashboard/Dashboard';
import InvoicesPageWrapper from '@/pages/invoices/InvoicesPageWrapper';
import InvoicingCompaniesPage from '@/pages/invoicing-companies/InvoicingCompaniesPage';
import MondayRedirect from '@/pages/MondayRedirect';
import OffersPage from '@/pages/offers/OffersPage';
import PayerDetail from '@/pages/payers/PayerDetail';
import PayersPage from '@/pages/payers/PayersPage';
import InboxPage from '@/pages/persons/InboxPage';
import LeadRoute from '@/pages/persons/LeadRoute';
import PeoplePage from '@/pages/persons/PeoplePage';
import Products from '@/pages/products/Products';
import ReferringAgentsPage from '@/pages/referring-agents/ReferringAgentsPage';
import RenewalsPage from '@/pages/renewals/RenewalsPage';
import JurisdictionFeesPage from '@/pages/settings/JurisdictionFeesPage';
import SplitPlansPage from '@/pages/split-plans/SplitPlansPage';

export interface RouteConfig {
  path: string;
  component: ComponentType<any>;
  permissions?: Permission[];
  requireAll?: boolean;

  /**
   * Optional menu configuration. If provided, this route will appear in the navigation menu.
   */
  menu?: {
    label: string;
    icon: ComponentType;

    /**
     * Optional nav-counter key. When set, the menu entry renders a badge with
     * the matching count from `useNavCounters()` (hidden when zero or absent).
     */
    badgeKey?: NavCounterKey;

    permissions?: Permission[];
  };
}

/**
 * Application route configuration
 *
 * Each route can specify:
 * - permissions: Array of required permissions
 * - requireAll: If true, user must have ALL permissions. If false, user needs ANY one permission.
 * - menu: If provided, the route will appear in the navigation menu with the specified label and icon
 *
 * Routes without permissions are accessible to all authenticated users.
 */
export const routes: RouteConfig[] = [
  {
    path: '/dashboard',
    component: Dashboard,
    permissions: [PERMISSIONS.DASHBOARD_READ],
    menu: {
      label: 'Dashboard',
      icon: MdOutlineDashboard,
    },
  },
  {
    path: '/offers',
    component: OffersPage,
    permissions: [
      PERMISSIONS.OFFERS_READ,
      PERMISSIONS.OFFERS_SEND,
      PERMISSIONS.OFFERS_FULFILL,
    ],
    requireAll: false,
    menu: {
      label: 'Offers',
      permissions: OFFER_LIST_PERMISSIONS,
      icon: PiBagSimple,
      badgeKey: 'offersAwaitingSalesRep',
    },
  },
  {
    path: '/split-plans',
    component: SplitPlansPage,
    permissions: ['offers:approve-split' as Permission],
    menu: {
      label: 'Split Plans',
      icon: PiCreditCard,
      badgeKey: 'splitPlansPendingReview',
    },
  },
  {
    path: '/contacts/:id',
    component: ContactDetailPage,
    permissions: [
      PERMISSIONS.CONTACTS_READ,
      'contacts:read-all' as Permission,
      PERMISSIONS.CONTACTS_ALL,
    ],
  },
  {
    path: '/accounts/:id',
    component: AccountDetailPage,
    permissions: [
      PERMISSIONS.CONTACTS_READ,
      'contacts:read-all' as Permission,
      PERMISSIONS.CONTACTS_ALL,
    ],
  },
  {
    path: '/accounts',
    component: AccountsListPage,
    permissions: [
      PERMISSIONS.CONTACTS_READ,
      'contacts:read-all' as Permission,
      PERMISSIONS.CONTACTS_ALL,
    ],
    menu: {
      label: 'Accounts',
      icon: PiBuildingsLight,
    },
  },
  {
    path: '/people',
    component: PeoplePage,
    permissions: [
      PERMISSIONS.LEADS_READ,
      'leads:read-all' as Permission,
      PERMISSIONS.LEADS_ALL,
      PERMISSIONS.CONTACTS_READ,
      'contacts:read-all' as Permission,
      PERMISSIONS.CONTACTS_ALL,
    ],
    menu: {
      label: 'Contacts',
      icon: PiUsersFourLight,
    },
  },
  {
    path: '/inbox',
    component: InboxPage,
    permissions: [
      'leads:assign' as Permission,
      'leads:read-all' as Permission,
      PERMISSIONS.LEADS_ALL,
    ],
    menu: {
      label: 'Inbox',
      icon: PiUserPlusLight,
    },
  },
  {
    path: '/contacts',
    component: PeoplePage,
    permissions: [
      PERMISSIONS.CONTACTS_READ,
      'contacts:read-all' as Permission,
      PERMISSIONS.CONTACTS_ALL,
    ],
  },
  {
    path: '/contacts-monday',
    component: Contacts,
    permissions: [PERMISSIONS.CONTACTS_READ],
  },
  {
    path: '/leads/:id',
    component: LeadRoute,
    permissions: [
      PERMISSIONS.LEADS_READ,
      'leads:read-all' as Permission,
      PERMISSIONS.LEADS_ALL,
    ],
  },
  {
    path: '/leads',
    component: PeoplePage,
    permissions: [
      PERMISSIONS.LEADS_READ,
      'leads:read-all' as Permission,
      PERMISSIONS.LEADS_ALL,
    ],
  },
  {
    path: '/referring-agents',
    component: ReferringAgentsPage,
    permissions: [REFERRING_AGENTS_READ, REFERRING_AGENTS_ALL],
    menu: {
      label: 'Referring Agents',
      icon: PiHandshake,
    },
  },
  {
    path: '/client-companies/:id',
    component: ClientCompanyPage,
    permissions: CLIENT_PORTFOLIO_VIEW,
  },
  {
    path: '/monday/:itemId',
    component: MondayRedirect,
  },
  {
    path: '/client-contacts/:contactId',
    component: ClientContactPortfolioPage,
    permissions: CLIENT_PORTFOLIO_VIEW,
  },
  {
    path: '/client-companies',
    component: ClientCompaniesPage,
    permissions: CLIENT_PORTFOLIO_VIEW,
    menu: {
      label: 'Client Companies',
      icon: PiBriefcase,
    },
  },
  {
    path: '/companies',
    component: Companies,
    permissions: [PERMISSIONS.COMPANIES_READ],
    menu: {
      label: 'Companies',
      icon: PiBagSimple,
    },
  },
  {
    path: '/products',
    component: Products,
    permissions: [PERMISSIONS.SERVICES_READ],
    menu: {
      label: 'Product List',
      icon: PiBagSimple,
    },
  },
  {
    path: '/invoices',
    component: InvoicesPageWrapper,
    permissions: [PERMISSIONS.INVOICES_READ],
    menu: {
      label: 'Invoices',
      icon: PiReceipt,
    },
  },
  {
    path: '/commercial-proposals',
    component: CommercialProposalsPage,
    permissions: [PERMISSIONS.PROPOSALS_READ, PROPOSALS_READ_SHOP],
    requireAll: false,
    menu: {
      label: 'Commercial Proposals',
      icon: PiBagSimple,
    },
  },
  {
    path: '/renewals',
    component: RenewalsPage,
    permissions: [PERMISSIONS.RENEWALS_READ],
    menu: {
      label: 'Renewals',
      icon: PiReceipt,
    },
  },
  {
    path: '/invoicing-companies',
    component: InvoicingCompaniesPage,
    permissions: [INVOICING_COMPANIES_ALL as Permission],
    menu: {
      label: 'Invoicing Companies',
      icon: PiBuildingsLight,
    },
  },
  {
    path: '/payers/:id',
    component: PayerDetail,
    permissions: [PERMISSIONS.PAYERS_READ, PAYERS_ALL as Permission],
  },
  {
    path: '/payers',
    component: PayersPage,
    permissions: [PERMISSIONS.PAYERS_READ, PAYERS_ALL as Permission],
    menu: { label: 'Payers', icon: PiShieldCheck },
  },
  {
    path: '/settings/jurisdiction-fees',
    component: JurisdictionFeesPage,
    permissions: [PERMISSIONS.RENEWALS_WRITE],
    menu: {
      label: 'Jurisdiction Fees',
      icon: MdOutlineSettings,
    },
  },
  {
    path: '/admin/users',
    component: Users,
    permissions: [PERMISSIONS.RBAC_READ],
    menu: {
      label: 'Users',
      icon: PiUsersFourLight,
    },
  },
  {
    path: '/admin/roles',
    component: Roles,
    permissions: [PERMISSIONS.RBAC_READ],
    menu: {
      label: 'Roles',
      icon: PiShieldCheck,
    },
  },
];

export interface MenuItem {
  label: string;
  to: string;
  icon: ComponentType;
  badgeKey?: NavCounterKey;
  permissions?: Permission[];
  requireAll?: boolean;
}

export const menuItems: MenuItem[] = routes
  .filter((route) => route.menu)
  .map((route) => ({
    label: route.menu!.label,
    to: route.path,
    icon: route.menu!.icon,
    badgeKey: route.menu!.badgeKey,
    permissions: route.menu!.permissions ?? route.permissions,
    requireAll: route.requireAll,
  }));

export interface MenuGroup {
  title: string | null;
  items: MenuItem[];
}

const GROUPS: { title: string | null; paths: string[] }[] = [
  { title: null, paths: ['/dashboard'] },
  {
    title: 'CRM',
    paths: [
      '/people',
      '/inbox',
      '/accounts',
      '/client-companies',
      '/companies',
      '/referring-agents',
    ],
  },
  {
    title: 'Sales',
    paths: ['/offers', '/split-plans', '/commercial-proposals', '/renewals'],
  },
  { title: 'Billing', paths: ['/invoices', '/invoicing-companies', '/payers'] },
  { title: 'Catalog', paths: ['/products', '/settings/jurisdiction-fees'] },
  { title: 'Administration', paths: ['/admin/users', '/admin/roles'] },
];

const HIDDEN_MENU_PATHS = ['/inbox', '/client-companies', '/renewals'];

export const menuGroups: MenuGroup[] = GROUPS.map((group) => ({
  title: group.title,
  items: group.paths
    .filter((path) => !HIDDEN_MENU_PATHS.includes(path))
    .map((path) => menuItems.find((item) => item.to === path))
    .filter((item): item is MenuItem => !!item),
}));
